/**
 * 追踪码 Context
 * 用于管理追踪码，支持 SSR
 *
 * 追踪码来源优先级：
 * 1. URL 参数 ?tc=xxx（任何时候只要带入就立即替换）
 * 2. localStorage（后续访问）
 */

import { createContext, useContext, useMemo, useEffect, useState, ReactNode } from 'react';

/**
 * 追踪码上下文类型
 */
interface TrackingCodeContextType {
  /** 当前追踪码 */
  trackingCode: string | null;
  /** 是否已初始化（客户端已从 localStorage 读取） */
  initialized: boolean;
}

const TrackingCodeContext = createContext<TrackingCodeContextType | null>(null);

interface TrackingCodeProviderProps {
  children: ReactNode;
  /** SSR 时从 URL 参数获取的追踪码 */
  trackingCodeFromUrl?: string | null;
}

/**
 * 从当前 URL 获取追踪码参数
 */
function getTrackingCodeFromCurrentUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('tc');
}

/**
 * 同步初始化追踪码（在渲染时执行，不是 useEffect）
 */
function initTrackingCode(trackingCodeFromUrl: string | null | undefined): string | null {
  // SSR 环境
  if (typeof window === 'undefined') {
    return trackingCodeFromUrl ?? null;
  }

  // 客户端：优先从当前 URL 获取（处理客户端路由导航情况）
  const urlCode = getTrackingCodeFromCurrentUrl() || trackingCodeFromUrl;

  // 如果 URL 有追踪码，存入 localStorage
  if (urlCode) {
    localStorage.setItem('trackingCode', urlCode);
    return urlCode;
  }

  // 否则从 localStorage 读取
  return localStorage.getItem('trackingCode');
}

/**
 * 追踪码 Provider
 *
 * 工作流程：
 * 1. SSR 时：从 URL 参数获取追踪码
 * 2. 客户端 hydration：同步初始化，立即存入 localStorage
 * 3. 客户端路由变化：监听 URL 变化，如果有新追踪码则立即替换
 */
export function TrackingCodeProvider({
  children,
  trackingCodeFromUrl,
}: TrackingCodeProviderProps) {
  // 同步初始化追踪码
  const initialCode = initTrackingCode(trackingCodeFromUrl);
  const [trackingCode, setTrackingCode] = useState<string | null>(initialCode);

  // 客户端始终是 initialized，SSR 环境不需要
  const initialized = typeof window !== 'undefined';

  // 监听 URL 变化（客户端路由导航时检查追踪码）
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // 检查并更新追踪码的函数
    const checkAndUpdateTrackingCode = () => {
      const urlCode = getTrackingCodeFromCurrentUrl();
      if (urlCode) {
        // URL 有追踪码，立即替换
        localStorage.setItem('trackingCode', urlCode);
        setTrackingCode(urlCode);
      }
    };

    // 监听 popstate（浏览器前进/后退）
    window.addEventListener('popstate', checkAndUpdateTrackingCode);

    // 监听 pushstate/replacestate（vike 路由导航）
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      checkAndUpdateTrackingCode();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      checkAndUpdateTrackingCode();
    };

    return () => {
      window.removeEventListener('popstate', checkAndUpdateTrackingCode);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // 没有房间编码时跳转到错误页面
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // 如果已经在 /room-closed 页面，不需要再跳转
    if (window.location.pathname === '/room-closed') {
      return;
    }

    // 如果没有房间编码，跳转到错误页面
    if (!trackingCode) {
      window.location.href = '/room-closed';
    }
  }, [trackingCode]);

  const value = useMemo(() => ({
    trackingCode,
    initialized,
  }), [trackingCode, initialized]);

  return (
    <TrackingCodeContext.Provider value={value}>
      {children}
    </TrackingCodeContext.Provider>
  );
}

/**
 * 使用追踪码的 Hook
 * @throws Error 如果在 TrackingCodeProvider 外部使用
 */
export function useTrackingCode(): TrackingCodeContextType {
  const context = useContext(TrackingCodeContext);
  if (!context) {
    throw new Error('useTrackingCode must be used within TrackingCodeProvider');
  }
  return context;
}

/**
 * 获取追踪码（非 hook 方式，用于 API 请求等）
 * 仅在客户端有效
 */
export function getTrackingCode(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem('trackingCode');
}
